import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mono = "'Space Mono', monospace";
const serifDisplay = "'Libre Caslon Display', serif";
const highlightBg = (a) => `linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(${a}) 62%, var(--paper)) 5%, color-mix(in oklch, var(${a}) 46%, var(--paper)) 52%, color-mix(in oklch, var(${a}) 68%, var(--paper)) 94%, transparent 98.5%)`;
const card = (accent) => ({ position: 'relative', border: '1px solid var(--line)', borderTop: `6px solid var(${accent})`, background: 'var(--paper)', padding: '28px 30px' });
const monoLabel = { fontFamily: mono, fontWeight: 700, fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase' };
const monoBtn = (bg, fg) => ({ cursor: 'pointer', border: '1px solid var(--line)', background: bg, color: fg, fontFamily: mono, fontWeight: 700, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', padding: '9px 18px' });
const highlightBtn = (a) => ({ display: 'block', width: '100%', cursor: 'pointer', border: 'none', textAlign: 'center', fontFamily: mono, fontWeight: 700, fontSize: 13, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--ink)', padding: 13, background: highlightBg(a) });
const inputStyle = { boxSizing: 'border-box', border: '1px solid var(--line)', background: 'var(--bg)', padding: '10px 12px', fontSize: 14, color: 'var(--ink)', fontFamily: 'inherit' };
const overlay = { position: 'fixed', inset: 0, background: 'color-mix(in oklch, var(--ink) 55%, transparent)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 };
const modalBox = { background: 'var(--paper)', border: '1px solid var(--line)', boxShadow: '8px 9px 0 color-mix(in oklch, var(--line) 35%, transparent)', padding: '30px 32px', maxWidth: 480, width: '100%', maxHeight: '85vh', overflowY: 'auto' };

const fmtDMY = (iso) => { try { return new Date(iso).toLocaleDateString('en-GB'); } catch { return '—'; } };
const fmtShort = (iso) => { try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); } catch { return ''; } };
const fmtLong = (iso) => { try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return iso; } };

const getNextThursday = (fromDate = new Date()) => {
    const date = new Date(fromDate);
    const day = date.getDay();
    const daysUntilThursday = (4 - day + 7) % 7;
    if (daysUntilThursday === 0 && date.getHours() >= 12) date.setDate(date.getDate() + 7);
    else date.setDate(date.getDate() + (daysUntilThursday === 0 ? 7 : daysUntilThursday));
    return date;
};
const getUpcomingThursdays = () => {
    const out = [];
    let cur = getNextThursday();
    for (let i = 0; i < 4; i++) { out.push(new Date(cur)); cur.setDate(cur.getDate() + 7); }
    return out;
};

const TXN_LABEL = {
    delivery_charge: 'delivery charge', cash_topup: 'cash top-up', upi_topup: 'UPI top-up',
    admin_topup: 'admin top-up', razorpay_topup: 'online top-up', admin_adjustment: 'adjustment',
};

function Row({ label, value, valueColor }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, borderBottom: '1px dashed var(--line)', padding: '10px 0' }}>
            <span style={{ fontFamily: "'Libre Caslon Text', serif", fontSize: 16 }}>{label}</span>
            <span style={{ ...monoLabel, fontSize: 12, color: valueColor || 'var(--ink)' }}>{value}</span>
        </div>
    );
}

function PaymentsPanel({ initialPlan, initialChildName, initialAdults = 0, initialChildren = 0 }) {
    const { user, getAuthHeader, refreshUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(initialPlan === 'annual' ? 'annual' : 'monthly');
    const [childPlan, setChildPlan] = useState(['child_monthly', 'child_annual'].includes(initialPlan) ? initialPlan : null);
    const [childName, setChildName] = useState(initialChildName || '');
    const [paymentMode, setPaymentMode] = useState(String(initialPlan || '').startsWith('child_') ? 'one-time' : 'autopay');
    const [subscriptionDetails, setSubscriptionDetails] = useState(null);
    const [cancellingAutopay, setCancellingAutopay] = useState(false);

    // Delivery balance
    const [delivery, setDelivery] = useState(null);
    const [topupAmount, setTopupAmount] = useState('');
    const [topupSubmitting, setTopupSubmitting] = useState(false);
    const [topupError, setTopupError] = useState('');

    // Pause
    const [pauseEligibility, setPauseEligibility] = useState(null);
    const [myPauses, setMyPauses] = useState([]);
    const [pauseModalOpen, setPauseModalOpen] = useState(false);
    const [pauseStart, setPauseStart] = useState('');
    const [pauseEnd, setPauseEnd] = useState('');
    const [pauseReason, setPauseReason] = useState('');
    const [pauseSubmitting, setPauseSubmitting] = useState(false);
    const [pauseError, setPauseError] = useState('');

    // Cancel membership (moved from Profile)
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [cancelMsg, setCancelMsg] = useState('');

    // Pickups (moved from Profile)
    const [scheduledPickups, setScheduledPickups] = useState([]);
    const [showPickupModal, setShowPickupModal] = useState(false);
    const [selectedPickupDate, setSelectedPickupDate] = useState(null);
    const [pickupAddress, setPickupAddress] = useState('');
    const [schedulingPickup, setSchedulingPickup] = useState(false);
    const [pickupError, setPickupError] = useState('');

    // Gifts
    const [receivedGifts, setReceivedGifts] = useState([]);
    const [redeemingGiftId, setRedeemingGiftId] = useState(null);

    const notifyUpdated = () => window.dispatchEvent(new Event('payments-updated'));

    const fetchSubscriptionDetails = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/subscriptions/status`, { headers: getAuthHeader() });
            setSubscriptionDetails(res.data);
        } catch { /* ignore */ }
    }, [getAuthHeader]);

    const fetchDelivery = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/me/delivery-balance`, { headers: getAuthHeader() });
            setDelivery(res.data);
        } catch { /* ignore */ }
    }, [getAuthHeader]);

    const fetchPauses = useCallback(async () => {
        try {
            const [el, mp] = await Promise.all([
                axios.get(`${API}/subscriptions/pause-eligibility`, { headers: getAuthHeader() }),
                axios.get(`${API}/subscriptions/my-pauses`, { headers: getAuthHeader() }),
            ]);
            setPauseEligibility(el.data);
            setMyPauses(mp.data || []);
        } catch { /* ignore */ }
    }, [getAuthHeader]);

    const fetchPickups = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/pickups/my-pickups`, { headers: getAuthHeader() });
            setScheduledPickups(res.data || []);
        } catch { /* ignore */ }
    }, [getAuthHeader]);

    const fetchGifts = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/gifts/received`, { headers: getAuthHeader() });
            setReceivedGifts(res.data || []);
        } catch { /* ignore */ }
    }, [getAuthHeader]);

    useEffect(() => {
        fetchSubscriptionDetails();
        fetchDelivery();
        fetchPickups();
        fetchGifts();
        fetchLateFees();
        if (user?.subscription_status === 'active') fetchPauses();
        if (String(initialPlan || '').startsWith('bundle_') && (initialAdults + initialChildren) > 0) {
            handlePayment(initialPlan, { adults: initialAdults, children: initialChildren });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    // ===== Late fees =====
    const [lateFees, setLateFees] = useState({ late_fees: [], total_outstanding: 0 });
    const [payingFees, setPayingFees] = useState(false);
    async function fetchLateFees() {
        try {
            const res = await axios.get(`${API}/late-fees/my-fees`, { headers: getAuthHeader() });
            setLateFees(res.data || { late_fees: [], total_outstanding: 0 });
        } catch { /* non-blocking */ }
    }
    const handlePayLateFees = async () => {
        setPayingFees(true);
        try {
            const orderRes = await axios.post(`${API}/late-fees/create-payment-order`, {}, { headers: getAuthHeader() });
            const { order_id, amount, currency, key_id } = orderRes.data;
            const rzp = new window.Razorpay({
                key: key_id, amount, currency, order_id,
                name: 'The Hyd Library',
                description: `Late fee payment - ₹${(amount / 100).toFixed(0)}`,
                handler: async (response) => {
                    try {
                        await axios.post(`${API}/late-fees/verify-payment`, {
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_signature: response.razorpay_signature,
                        }, { headers: getAuthHeader() });
                        alert('Late fees paid — you can borrow again!');
                        await refreshUser();
                        fetchLateFees();
                    } catch {
                        alert('Payment verification failed. Please contact support.');
                    }
                },
                prefill: { name: user?.name, email: user?.email, contact: user?.phone || '' },
                theme: { color: '#31435f' },
                modal: { ondismiss: () => setPayingFees(false), escape: true, animation: true },
            });
            rzp.on('payment.failed', (resp) => { alert(`Payment failed: ${resp.error?.description || 'Unknown error'}.`); setPayingFees(false); });
            rzp.open();
        } catch (err) {
            alert(err.response?.data?.detail || 'Failed to start fee payment');
        } finally {
            setPayingFees(false);
        }
    };

    // ===== Subscription payments =====
    const handlePayment = async (planType, opts = {}) => {
        const needsChildName = planType.startsWith('child_') || (planType.startsWith('bundle_') && (opts.children || 0) > 0);
        if (needsChildName && !childName.trim() && !user?.child_name) {
            alert("Please enter your child's name first.");
            return;
        }
        setLoading(true);
        try {
            const orderBody = { plan: planType };
            if (planType.startsWith('child_')) orderBody.child_name = childName.trim() || user?.child_name;
            if (planType.startsWith('bundle_')) {
                orderBody.adult_count = opts.adults || 0;
                orderBody.child_count = opts.children || 0;
                if ((opts.children || 0) > 0) orderBody.child_name = childName.trim() || user?.child_name;
            }
            const orderRes = await axios.post(`${API}/payments/create-order`, orderBody, { headers: getAuthHeader() });
            const { order_id, amount, currency, key_id } = orderRes.data;
            const rzp = new window.Razorpay({
                key: key_id, amount, currency, order_id,
                name: 'The Hyd Library',
                description: {
                    monthly: 'Monthly Subscription - ₹500',
                    annual: 'Annual Subscription - ₹5,000',
                    child_monthly: "Children's Membership (Monthly) - ₹300",
                    child_annual: "Children's Membership (Annual) - ₹3,000",
                }[planType] || 'Membership payment',
                handler: async (response) => {
                    try {
                        await axios.post(`${API}/payments/verify`, {
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_signature: response.razorpay_signature,
                        }, { headers: getAuthHeader() });
                        alert('Payment successful! Your subscription is now active.');
                        await refreshUser();
                        fetchSubscriptionDetails();
                        notifyUpdated();
                    } catch {
                        alert('Payment verification failed. Please contact support.');
                    }
                },
                prefill: { name: user?.name, email: user?.email, contact: user?.phone || '' },
                theme: { color: '#31435f' },
                modal: { ondismiss: () => setLoading(false), escape: true, animation: true },
                retry: { enabled: true, max_count: 3 },
            });
            rzp.on('payment.failed', (resp) => { alert(`Payment failed: ${resp.error?.description || 'Unknown error'}. Please try again.`); setLoading(false); });
            rzp.open();
        } catch (err) {
            alert(err.response?.data?.detail || 'Failed to create payment order');
        } finally {
            setLoading(false);
        }
    };

    const handleAutopaySubscription = async (planType) => {
        setLoading(true);
        try {
            const subRes = await axios.post(`${API}/subscriptions/create`, { plan: planType }, { headers: getAuthHeader() });
            const { subscription_id } = subRes.data;
            const keyRes = await axios.post(`${API}/payments/create-order`, { plan: planType }, { headers: getAuthHeader() });
            const rzp = new window.Razorpay({
                key: keyRes.data.key_id, subscription_id,
                name: 'The Hyd Library',
                description: planType === 'annual' ? 'Annual Subscription (Autopay) - ₹5,000/year' : 'Monthly Subscription (Autopay) - ₹500/month',
                handler: async (response) => {
                    try {
                        await axios.post(`${API}/subscriptions/verify`, {
                            subscription_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                        }, { headers: getAuthHeader() });
                        alert('Autopay activated! Your subscription will auto-renew. You can cancel anytime.');
                        await refreshUser();
                        fetchSubscriptionDetails();
                        notifyUpdated();
                    } catch {
                        alert('Subscription verification failed. Please contact support.');
                    }
                },
                prefill: { name: user?.name, email: user?.email, contact: user?.phone || '' },
                theme: { color: '#31435f' },
                modal: { ondismiss: () => setLoading(false) },
            });
            rzp.open();
        } catch (err) {
            alert(err.response?.data?.detail || 'Failed to create autopay subscription');
            setLoading(false);
        }
    };

    const handleCancelAutopay = async () => {
        if (!window.confirm('Cancel autopay? Your subscription stays active until the end of the current period.')) return;
        setCancellingAutopay(true);
        try {
            const res = await axios.post(`${API}/subscriptions/cancel`, {}, { headers: getAuthHeader() });
            alert(res.data.message);
            await refreshUser();
            fetchSubscriptionDetails();
        } catch (err) {
            alert(err.response?.data?.detail || 'Failed to cancel autopay');
        } finally {
            setCancellingAutopay(false);
        }
    };

    // ===== Delivery top-up =====
    const handleTopup = async (overrideAmount) => {
        const amt = parseFloat(overrideAmount ?? topupAmount);
        if (!amt || amt < 50) { setTopupError('Minimum top-up is ₹50'); return; }
        setTopupError('');
        setTopupSubmitting(true);
        try {
            const orderRes = await axios.post(`${API}/delivery/topup/create-order`, { amount: amt }, { headers: getAuthHeader() });
            const rzp = new window.Razorpay({
                key: orderRes.data.key_id, amount: orderRes.data.amount, currency: orderRes.data.currency, order_id: orderRes.data.order_id,
                name: 'The Hyderabad Library', description: 'Delivery balance top-up',
                prefill: { name: user?.name || '', email: user?.email || '', contact: user?.phone || '' },
                theme: { color: '#31435f' },
                handler: async (resp) => {
                    try {
                        const v = await axios.post(`${API}/delivery/topup/verify-payment`, {
                            razorpay_order_id: resp.razorpay_order_id,
                            razorpay_payment_id: resp.razorpay_payment_id,
                            razorpay_signature: resp.razorpay_signature,
                        }, { headers: getAuthHeader() });
                        alert(v.data.message || 'Top-up successful');
                        setTopupAmount('');
                        fetchDelivery();
                        notifyUpdated();
                    } catch (verr) {
                        setTopupError(verr.response?.data?.detail || 'Verification failed');
                    } finally {
                        setTopupSubmitting(false);
                    }
                },
                modal: { ondismiss: () => setTopupSubmitting(false) },
            });
            rzp.on('payment.failed', () => { setTopupError('Payment failed'); setTopupSubmitting(false); });
            rzp.open();
        } catch (err) {
            setTopupError(err.response?.data?.detail || 'Failed to start top-up');
            setTopupSubmitting(false);
        }
    };

    // ===== Pause =====
    const submitPauseRequest = async () => {
        setPauseError('');
        if (!pauseStart || !pauseEnd) { setPauseError('Please choose start and end dates.'); return; }
        setPauseSubmitting(true);
        try {
            await axios.post(`${API}/subscriptions/pause-request`, { start_date: pauseStart, end_date: pauseEnd, reason: pauseReason }, { headers: getAuthHeader() });
            setPauseModalOpen(false);
            setPauseStart(''); setPauseEnd(''); setPauseReason('');
            await fetchPauses();
            alert('Pause request submitted. An admin will review it shortly.');
        } catch (err) {
            setPauseError(err.response?.data?.detail || 'Failed to submit pause request.');
        } finally {
            setPauseSubmitting(false);
        }
    };

    const cancelMyPause = async (pauseId) => {
        if (!window.confirm('Cancel this pending pause request?')) return;
        try {
            await axios.delete(`${API}/subscriptions/pauses/${pauseId}`, { headers: getAuthHeader() });
            await fetchPauses();
        } catch (err) {
            alert(err.response?.data?.detail || 'Failed to cancel pause');
        }
    };

    // ===== Cancel membership =====
    const handleCancelSubscription = async () => {
        setCancelling(true);
        try {
            const res = await axios.post(`${API}/users/cancel-subscription`, {}, { headers: getAuthHeader() });
            setCancelMsg(`${res.data.message} ${res.data.refund_note || ''}`);
            setShowCancelModal(false);
            if (refreshUser) refreshUser();
        } catch (err) {
            alert(err.response?.data?.detail || 'Failed to cancel subscription');
        } finally {
            setCancelling(false);
        }
    };

    // ===== Pickups =====
    const handleSchedulePickup = async () => {
        if (!selectedPickupDate) { setPickupError('Please select a Thursday for pickup'); return; }
        if (!pickupAddress.trim()) { setPickupError('Please enter your address'); return; }
        setSchedulingPickup(true);
        setPickupError('');
        try {
            await axios.post(`${API}/pickups/schedule`, { pickup_date: selectedPickupDate.toISOString().split('T')[0], address: pickupAddress.trim() }, { headers: getAuthHeader() });
            setShowPickupModal(false);
            setSelectedPickupDate(null);
            fetchPickups();
        } catch (err) {
            setPickupError(err.response?.data?.detail || 'Failed to schedule pickup');
        } finally {
            setSchedulingPickup(false);
        }
    };

    const handleCancelPickup = async (pickupId) => {
        if (!window.confirm('Cancel this pickup?')) return;
        try {
            await axios.delete(`${API}/pickups/${pickupId}`, { headers: getAuthHeader() });
            fetchPickups();
        } catch (err) {
            alert(err.response?.data?.detail || 'Failed to cancel pickup');
        }
    };

    // ===== Gifts =====
    const redeemGift = async (giftId) => {
        if (!window.confirm('Redeem this gift now? It will extend your subscription.')) return;
        setRedeemingGiftId(giftId);
        try {
            const res = await axios.post(`${API}/gifts/${giftId}/redeem`, {}, { headers: getAuthHeader() });
            alert(`${res.data.message} New end date: ${fmtLong(res.data.subscription_end_date)}`);
            await fetchGifts();
            await refreshUser();
            fetchSubscriptionDetails();
        } catch (err) {
            alert(err.response?.data?.detail || 'Redemption failed');
        } finally {
            setRedeemingGiftId(null);
        }
    };

    const isActive = user?.subscription_status === 'active';
    const isCancelled = user?.subscription_cancelled;
    const endDate = user?.subscription_end_date;
    const daysRemaining = endDate ? Math.max(0, Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24))) : 0;
    const isAutopay = subscriptionDetails?.autopay_active || subscriptionDetails?.subscription_type === 'autopay';

    const owes = delivery?.owes_money;
    const owed = delivery?.amount_owed ?? 0;
    const balance = delivery?.balance ?? 0;
    const PLAN_META = {
        monthly: { price: '₹500', unit: 'month' },
        annual: { price: '₹5,000', unit: 'year' },
        child_monthly: { price: '₹300', unit: 'month' },
        child_annual: { price: '₹3,000', unit: 'year' },
    };
    const planPrice = (PLAN_META[selectedPlan] || PLAN_META.monthly).price;
    const planUnit = (PLAN_META[selectedPlan] || PLAN_META.monthly).unit;
    const pauseAllowed = pauseEligibility?.eligible !== false;
    const redeemableGifts = receivedGifts.filter(g => g.status === 'pending' || g.status === 'unredeemed' || !g.redeemed_at);

    const ctaLabel = loading ? 'Opening checkout…'
        : paymentMode === 'autopay' ? `Enable autopay — ${planPrice}/${planUnit}`
        : isActive ? `Renew now — ${planPrice}`
        : `Subscribe — ${planPrice}`;

    return (
        <div style={{ color: 'var(--ink)' }}>
            {cancelMsg && <div style={{ border: '1px solid var(--line)', borderLeft: '6px solid var(--a3)', background: 'var(--paper)', padding: '12px 16px', marginBottom: 20, fontSize: 14.5 }} data-testid="cancel-membership-msg">{cancelMsg}</div>}

            <div className="hydlib-cols" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28, alignItems: 'start', marginBottom: 28 }}>
                {/* LEFT — Current subscription */}
                <div style={card('--a4')} data-testid="subscription-card">
                    <h3 style={{ fontFamily: serifDisplay, fontWeight: 400, fontSize: 28, margin: '0 0 14px' }}>Current subscription</h3>
                    <Row label="Status" value={isActive ? '● ACTIVE' : '● INACTIVE'} valueColor={isActive ? 'var(--a3)' : 'var(--a4)'} />
                    {endDate && <Row label="Valid until" value={fmtDMY(endDate)} />}
                    {isActive && <Row label="Days remaining" value={`${daysRemaining} days`} />}
                    <Row label="Payment type" value={isAutopay ? 'AUTOPAY' : 'MANUAL'} />
                    {user?.membership_type === 'children' && (
                        <Row label="Type" value={`CHILDREN'S MEMBERSHIP${user?.child_name ? ` — ${user.child_name}` : ''}`} valueColor="var(--a5)" />
                    )}
                    {user?.child_membership_status === 'active' && user?.membership_type !== 'children' && (
                        <>
                            <Row label="Children's add-on" value={`● ACTIVE${user?.child_name ? ` — ${user.child_name}` : ''}`} valueColor="var(--a3)" />
                            {user?.child_membership_end_date && <Row label="Child valid until" value={fmtDMY(user.child_membership_end_date)} />}
                        </>
                    )}
                    {isCancelled && (
                        <div style={{ marginTop: 14, border: '1px dashed var(--a4)', background: 'color-mix(in oklch, var(--a4) 8%, var(--paper))', padding: '10px 14px', fontSize: 14 }}>
                            Cancellation scheduled — access continues until {endDate ? fmtDMY(endDate) : 'the period ends'}.
                        </div>
                    )}

                    {isAutopay ? (
                        <div style={{ marginTop: 20 }}>
                            <div style={{ ...monoLabel, color: 'var(--a3)', marginBottom: 8 }}>Autopay is on — renews automatically</div>
                            <button onClick={handleCancelAutopay} disabled={cancellingAutopay} data-testid="cancel-autopay-btn" style={{ cursor: 'pointer', border: 'none', background: 'none', color: 'var(--a4)', fontFamily: mono, fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', textDecoration: 'underline', padding: 0 }}>
                                {cancellingAutopay ? 'Cancelling…' : 'Cancel autopay'}
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* Autopay / one-time tabs */}
                            <div style={{ display: 'flex', marginTop: 20 }}>
                                {['autopay', 'one-time'].map(m => (
                                    <button key={m} onClick={() => setPaymentMode(m)} data-testid={`mode-${m}`}
                                        style={{ flex: 1, cursor: 'pointer', border: '1px solid var(--line)', background: paymentMode === m ? 'var(--ink)' : 'var(--paper)', color: paymentMode === m ? 'var(--paper)' : 'var(--ink)', fontFamily: mono, fontWeight: 700, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', padding: '11px 0' }}>
                                        {m === 'autopay' ? '⟳ Autopay' : 'One-time'}
                                    </button>
                                ))}
                            </div>
                            <p style={{ fontStyle: 'italic', fontSize: 14.5, margin: '12px 0 16px', opacity: 0.85 }}>
                                {paymentMode === 'autopay' ? 'Autopay renews via UPI or card. Cancel anytime — no guilt trips.' : 'Pay once — renew manually whenever it lapses.'}
                            </p>

                            {/* Plans */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                <button onClick={() => setSelectedPlan('monthly')} data-testid="plan-monthly"
                                    style={{ cursor: 'pointer', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, border: selectedPlan === 'monthly' ? '2px solid var(--a4)' : '1px solid var(--line)', background: 'var(--paper)', color: 'var(--ink)', padding: '16px 18px' }}>
                                    <span style={monoLabel}>{selectedPlan === 'monthly' ? '●' : '○'} Monthly</span>
                                    <span style={{ fontFamily: serifDisplay, fontSize: 24 }}>₹500<span style={{ fontSize: 15 }}>/month</span></span>
                                </button>
                                <button onClick={() => setSelectedPlan('annual')} data-testid="plan-annual"
                                    style={{ position: 'relative', cursor: 'pointer', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, border: selectedPlan === 'annual' ? '2px solid var(--a4)' : '1px solid var(--line)', background: 'var(--paper)', color: 'var(--ink)', padding: '16px 18px' }}>
                                    <span style={{ position: 'absolute', top: -11, right: 10, background: 'var(--a3)', color: 'var(--o3)', border: '1px solid var(--line)', fontFamily: mono, fontWeight: 700, fontSize: 10, letterSpacing: 1, padding: '2px 8px' }}>SAVE ₹1,000</span>
                                    <span style={monoLabel}>{selectedPlan === 'annual' ? '●' : '○'} Annual</span>
                                    <span style={{ fontFamily: serifDisplay, fontSize: 24 }}>₹5,000<span style={{ fontSize: 15 }}>/year</span> <s style={{ fontSize: 15, opacity: 0.55 }}>₹6,000</s></span>
                                </button>
                            </div>

                            <button
                                onClick={() => paymentMode === 'autopay' ? handleAutopaySubscription(selectedPlan) : handlePayment(selectedPlan)}
                                disabled={loading}
                                data-testid="subscription-pay-cta"
                                className="hydlib-highlight"
                                style={{ ...highlightBtn('--a4'), marginTop: 20, opacity: loading ? 0.7 : 1 }}
                            >
                                {ctaLabel}
                            </button>
                        </>
                    )}

                    {/* Children's membership — always visible, one-time purchase */}
                    <div style={{ marginTop: 22, borderTop: '1px dashed var(--line)', paddingTop: 16 }} data-testid="child-plans-block">
                        <div style={{ ...monoLabel, opacity: 0.7, marginBottom: 10 }}>
                            Children's membership — kids' books only{isActive ? ' (adds on to your plan)' : ''}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <button onClick={() => setChildPlan(childPlan === 'child_monthly' ? null : 'child_monthly')} data-testid="plan-child-monthly"
                                style={{ cursor: 'pointer', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, border: childPlan === 'child_monthly' ? '2px solid var(--a5)' : '1px solid var(--line)', background: 'var(--paper)', color: 'var(--ink)', padding: '16px 18px' }}>
                                <span style={monoLabel}>{childPlan === 'child_monthly' ? '●' : '○'} Children's monthly</span>
                                <span style={{ fontFamily: serifDisplay, fontSize: 24 }}>₹300<span style={{ fontSize: 15 }}>/month</span></span>
                            </button>
                            <button onClick={() => setChildPlan(childPlan === 'child_annual' ? null : 'child_annual')} data-testid="plan-child-annual"
                                style={{ cursor: 'pointer', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, border: childPlan === 'child_annual' ? '2px solid var(--a5)' : '1px solid var(--line)', background: 'var(--paper)', color: 'var(--ink)', padding: '16px 18px' }}>
                                <span style={monoLabel}>{childPlan === 'child_annual' ? '●' : '○'} Children's annual</span>
                                <span style={{ fontFamily: serifDisplay, fontSize: 24 }}>₹3,000<span style={{ fontSize: 15 }}>/year</span></span>
                            </button>
                            {childPlan && (
                                <>
                                    <input
                                        value={childName}
                                        onChange={(e) => setChildName(e.target.value)}
                                        placeholder={user?.child_name ? `Child's name (currently: ${user.child_name})` : "Child's name"}
                                        data-testid="child-name-input"
                                        style={{ border: '1px solid var(--line)', background: 'var(--paper)', color: 'var(--ink)', padding: '12px 14px', fontSize: 15, fontFamily: 'inherit' }}
                                    />
                                    <button onClick={() => handlePayment(childPlan)} disabled={loading} data-testid="child-plan-pay-cta" className="hydlib-highlight"
                                        style={{ ...highlightBtn('--a5'), opacity: loading ? 0.7 : 1 }}>
                                        {loading ? 'Opening checkout…' : `${isActive ? "Add children's membership" : "Children's membership"} — ${childPlan === 'child_annual' ? '₹3,000' : '₹300'}`}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* RIGHT — Thursday deliveries + pickups */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                    <div style={card('--a1')} data-testid="delivery-card">
                        <h3 style={{ fontFamily: serifDisplay, fontWeight: 400, fontSize: 28, margin: 0 }}>🚲 Thursday deliveries</h3>
                        <div style={{ fontFamily: "'Caveat', cursive", fontSize: 21, fontWeight: 600, color: 'var(--a1)', margin: '2px 0 16px', transform: 'rotate(-1deg)', width: 'fit-content' }}>charged by distance, paid after delivery</div>
                        <div style={{ border: '1px solid var(--line)', background: 'var(--paper)', padding: '16px 20px', marginBottom: 14 }}>
                            <div style={{ ...monoLabel, opacity: 0.7, marginBottom: 4 }}>{owes ? 'Due from past deliveries' : 'Prepaid balance'}</div>
                            <div style={{ fontFamily: serifDisplay, fontSize: 38, color: owes ? 'var(--a4)' : 'var(--a1)' }} data-testid="delivery-amount">
                                ₹{(owes ? owed : balance).toFixed(2)}
                            </div>
                        </div>
                        <p style={{ fontSize: 15, lineHeight: 1.65, margin: '0 0 16px' }}>
                            The charge depends on your distance from the library, and you pay after the books arrive. The same trip carries your returns back.
                        </p>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                            <input type="number" min={50} step={50} value={topupAmount} onChange={(e) => setTopupAmount(e.target.value)} placeholder={owes ? String(Math.max(owed, 50).toFixed(0)) : '200'} data-testid="topup-amount-input" style={{ ...inputStyle, flex: 1, minWidth: 100 }} />
                            {[100, 200, 500].map(v => (
                                <button key={v} onClick={() => setTopupAmount(String(v))} data-testid={`quick-topup-${v}`} style={{ cursor: 'pointer', border: '1px solid var(--line)', background: 'var(--bg)', color: 'var(--ink)', fontFamily: mono, fontSize: 12, padding: '8px 12px' }}>₹{v}</button>
                            ))}
                        </div>
                        <button onClick={() => handleTopup(owes && !topupAmount ? Math.max(owed, 50) : undefined)} disabled={topupSubmitting} data-testid="topup-submit-btn" className="hydlib-highlight" style={{ ...highlightBtn('--a1'), opacity: topupSubmitting ? 0.7 : 1 }}>
                            {topupSubmitting ? 'Opening…' : 'Settle up — UPI or card'}
                        </button>
                        {topupError && <div style={{ color: 'var(--a4)', fontSize: 13, marginTop: 8 }} data-testid="topup-error">{topupError}</div>}

                        <div style={{ ...monoLabel, marginTop: 18, marginBottom: 8 }}>Recent activity</div>
                        {(delivery?.transactions || []).length === 0 ? (
                            <div style={{ fontSize: 14, fontStyle: 'italic', opacity: 0.65 }}>No delivery activity yet.</div>
                        ) : (
                            <div style={{ maxHeight: 170, overflowY: 'auto' }}>
                                {delivery.transactions.map(t => (
                                    <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, borderBottom: '1px dashed var(--line)', padding: '7px 0', fontSize: 14.5 }}>
                                        <span>₹{Math.abs(t.delta).toFixed(0)} {TXN_LABEL[t.type] || t.type} — {t.delta < 0 ? 'charged' : 'paid'}</span>
                                        <span style={{ fontFamily: mono, fontSize: 12, opacity: 0.7, whiteSpace: 'nowrap' }}>{fmtShort(t.created_at)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Schedule a pickup */}
                    <div style={card('--a2')} data-testid="pickups-card">
                        <h3 style={{ fontFamily: serifDisplay, fontWeight: 400, fontSize: 28, margin: '0 0 6px' }}>📦 Schedule a pickup</h3>
                        <p style={{ fontSize: 15, margin: '0 0 14px', opacity: 0.85 }}>Reserve a Thursday slot to pick up your books from the box.</p>
                        {scheduledPickups.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                                {scheduledPickups.map(p => (
                                    <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, borderBottom: '1px dashed var(--line)', padding: '7px 0', fontSize: 14.5 }} data-testid={`pickup-${p.id}`}>
                                        <span style={{ fontFamily: mono, fontSize: 13 }}>{fmtLong(p.pickup_date)}{p.address ? <span style={{ display: 'block', fontSize: 11, opacity: 0.65 }}>{p.address}</span> : null}</span>
                                        <button onClick={() => handleCancelPickup(p.id)} data-testid={`cancel-pickup-${p.id}`} style={{ cursor: 'pointer', border: 'none', background: 'none', color: 'var(--a4)', fontFamily: mono, fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', textDecoration: 'underline', padding: 0 }}>Cancel</button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <button onClick={() => { setShowPickupModal(true); setPickupError(''); setPickupAddress(user?.address || scheduledPickups.find(p => p.address)?.address || ''); }} data-testid="schedule-pickup-button" style={monoBtn('var(--a2)', 'var(--o2)')}>+ Schedule new pickup</button>
                    </div>

                    {/* Outstanding late fees */}
                    <div style={card(((user?.outstanding_fees || 0) > 0 || (lateFees.total_accruing || 0) > 0) ? '--a4' : '--a3')} data-testid="late-fees-card">
                        <h3 style={{ fontFamily: serifDisplay, fontWeight: 400, fontSize: 28, margin: '0 0 6px' }}>Outstanding fees</h3>
                        {(lateFees.accruing || []).length > 0 && (
                            <div data-testid="accruing-fees-block" style={{ border: '1px dashed var(--a4)', padding: '10px 12px', marginBottom: 12 }}>
                                <p style={{ fontFamily: mono, fontWeight: 700, fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--a4)', margin: '0 0 6px' }}>
                                    Still growing — ₹{Number(lateFees.total_accruing || 0).toFixed(0)}
                                </p>
                                {lateFees.accruing.map((a) => (
                                    <div key={a.hold_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13.5, padding: '3px 0' }}>
                                        <span>{a.book_title}{a.is_child_hold ? ' (child)' : ''} — {a.days_overdue}d overdue</span>
                                        <span style={{ fontFamily: mono, fontSize: 12.5 }}>₹{a.fee_amount}</span>
                                    </div>
                                ))}
                                <p style={{ fontSize: 12.5, opacity: 0.75, margin: '6px 0 0', fontStyle: 'italic' }}>Grows ₹50/day per book — return the book to stop the meter. Payable once returned.</p>
                            </div>
                        )}
                        {(user?.outstanding_fees || 0) > 0 ? (
                            <>
                                <p style={{ fontSize: 15, margin: '0 0 12px', opacity: 0.85 }}>
                                    Late fees block new holds — clear them to keep borrowing. First 24h after a due date are always free.
                                </p>
                                <Row label="Amount due" value={`₹${Number(user.outstanding_fees).toFixed(2)}`} valueColor="var(--a4)" />
                                {lateFees.late_fees.length > 0 && (
                                    <div style={{ maxHeight: 130, overflowY: 'auto', margin: '10px 0' }}>
                                        {lateFees.late_fees.map(f => (
                                            <div key={f.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, borderBottom: '1px dashed var(--line)', padding: '6px 0', fontSize: 13.5 }}>
                                                <span>{f.days_overdue} day{f.days_overdue === 1 ? '' : 's'} late</span>
                                                <span style={{ fontFamily: mono, fontSize: 12.5 }}>₹{Number(f.fee_amount).toFixed(0)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <button
                                    onClick={handlePayLateFees}
                                    disabled={payingFees}
                                    data-testid="pay-late-fees-btn"
                                    style={{ ...monoBtn('var(--a4)', 'var(--o4)'), marginTop: 10, opacity: payingFees ? 0.7 : 1 }}
                                >
                                    {payingFees ? 'Opening checkout…' : `Pay ₹${Number(user.outstanding_fees).toFixed(0)} now`}
                                </button>
                            </>
                        ) : (
                            <p style={{ fontSize: 15, margin: 0, opacity: 0.8 }} data-testid="no-fees-msg">
                                {(lateFees.total_accruing || 0) > 0 ? 'No fees payable yet — return the overdue books above to settle up.' : "No outstanding fees — you're all clear. ✓"}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Pause + cancel membership */}
            {isActive && (
                <div style={{ ...card('--a3'), marginBottom: 28 }} data-testid="pause-card">
                    <h3 style={{ fontFamily: serifDisplay, fontWeight: 400, fontSize: 28, margin: '0 0 14px' }}>⏸ Pause membership</h3>
                    {!pauseAllowed ? (
                        <div style={{ border: '1px dashed var(--a4)', background: 'color-mix(in oklch, var(--a4) 8%, var(--paper))', padding: '14px 18px', fontSize: 15, lineHeight: 1.6 }} data-testid="pause-ineligible">
                            {pauseEligibility?.reason || 'Pauses aren\u2019t available on the one-time monthly plan. Switch to autopay or an annual plan to pause your membership.'}
                        </div>
                    ) : (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 15, opacity: 0.85 }}>Going away? Freeze your membership for a bit — your dates extend to match.</span>
                            <button onClick={() => { setPauseModalOpen(true); setPauseError(''); }} data-testid="request-pause-btn" style={monoBtn('var(--a3)', 'var(--o3)')}>Request a pause</button>
                        </div>
                    )}
                    {myPauses.length > 0 && (
                        <div style={{ marginTop: 16 }}>
                            <div style={{ ...monoLabel, marginBottom: 8 }}>Your pause requests</div>
                            {myPauses.map(p => (
                                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, borderBottom: '1px dashed var(--line)', padding: '7px 0', fontSize: 14.5 }} data-testid={`pause-${p.id}`}>
                                    <span>{fmtLong(p.start_date)} → {fmtLong(p.end_date)} · <span style={{ fontFamily: mono, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: p.status === 'approved' ? 'var(--a3)' : (p.status === 'rejected' ? 'var(--a4)' : 'var(--a5)') }}>{p.status}</span></span>
                                    {p.status === 'pending' && (
                                        <button onClick={() => cancelMyPause(p.id)} style={{ cursor: 'pointer', border: 'none', background: 'none', color: 'var(--a4)', fontFamily: mono, fontSize: 12, textDecoration: 'underline', padding: 0 }}>Cancel</button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Cancel membership — separate from pause */}
            {isActive && !isCancelled && (
                <div style={{ ...card('--a4'), marginBottom: 28 }} data-testid="cancel-membership-card">
                    <h3 style={{ fontFamily: serifDisplay, fontWeight: 400, fontSize: 28, margin: '0 0 14px' }}>Cancel membership</h3>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 15, opacity: 0.85 }}>Done for now? Access continues until your period ends — no further charges after that.</span>
                        <button onClick={() => setShowCancelModal(true)} data-testid="cancel-subscription-button" style={{ ...monoBtn('var(--paper)', 'var(--a4)'), borderColor: 'var(--a4)' }}>Cancel membership</button>
                    </div>
                </div>
            )}

            {/* Gift banner */}
            <div style={{ ...card('--a1'), display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 18 }} data-testid="gift-banner">
                <span style={{ position: 'absolute', top: -16, right: 14, fontFamily: "'Caveat', cursive", fontSize: 21, fontWeight: 600, color: 'var(--a1)', transform: 'rotate(-2deg)', background: 'var(--bg)', padding: '0 8px' }}>best gift ever, obviously</span>
                <div>
                    <div style={{ fontFamily: serifDisplay, fontSize: 26, marginBottom: 4 }}>🎁 Gift a membership to a friend</div>
                    <div style={{ fontSize: 15, opacity: 0.9 }}>Surprise them with curated books — they redeem it the next time they sign in.</div>
                </div>
                <Link to="/gift" className="hydlib-highlight" data-testid="gift-membership-link" style={{ fontFamily: mono, fontWeight: 700, fontSize: 13, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--ink)', background: highlightBg('--a1'), padding: '12px 24px' }}>
                    Gift a membership →
                </Link>
                {redeemableGifts.length > 0 && (
                    <div style={{ width: '100%', borderTop: '1px dashed var(--line)', paddingTop: 14 }}>
                        <div style={{ ...monoLabel, marginBottom: 8 }}>Gifts waiting for you</div>
                        {redeemableGifts.map(g => (
                            <div key={g.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '6px 0', fontSize: 14.5 }} data-testid={`gift-${g.id}`}>
                                <span>{g.plan === 'annual' ? 'Annual' : 'Monthly'} membership from {g.sender_name || 'a friend'}</span>
                                <button onClick={() => redeemGift(g.id)} disabled={redeemingGiftId === g.id} data-testid={`redeem-gift-${g.id}`} style={monoBtn('var(--a3)', 'var(--o3)')}>
                                    {redeemingGiftId === g.id ? 'Redeeming…' : 'Redeem →'}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Pickup modal */}
            {showPickupModal && (
                <div style={overlay} onClick={() => setShowPickupModal(false)}>
                    <div style={modalBox} onClick={(e) => e.stopPropagation()} data-testid="pickup-modal">
                        <h3 style={{ fontFamily: serifDisplay, fontWeight: 400, fontSize: 26, margin: '0 0 6px' }}>Pick a Thursday</h3>
                        <p style={{ fontSize: 14.5, opacity: 0.8, margin: '0 0 18px' }}>Pickups happen on Thursdays. Choose one:</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
                            {getUpcomingThursdays().map((thursday, i) => {
                                const selected = selectedPickupDate?.getTime() === thursday.getTime();
                                return (
                                    <button key={i} onClick={() => setSelectedPickupDate(thursday)} data-testid={`pickup-date-${i}`}
                                        style={{ cursor: 'pointer', textAlign: 'left', border: '1px solid var(--line)', background: selected ? 'var(--ink)' : 'var(--bg)', color: selected ? 'var(--paper)' : 'var(--ink)', fontFamily: mono, fontSize: 13, letterSpacing: 1, padding: '12px 16px' }}>
                                        {thursday.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                    </button>
                                );
                            })}
                        </div>
                        <div style={{ marginBottom: 14 }}>
                            <label style={{ display: 'block', fontFamily: mono, fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>Your address *</label>
                            <textarea value={pickupAddress} onChange={(e) => setPickupAddress(e.target.value)} rows={2} placeholder="Flat / street / area…" data-testid="pickup-address-input"
                                style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--line)', background: 'var(--bg)', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 14, padding: '10px 12px', resize: 'vertical' }} />
                        </div>
                        {pickupError && <div style={{ color: 'var(--a4)', fontSize: 14, marginBottom: 14 }} data-testid="pickup-error">{pickupError}</div>}
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={() => setShowPickupModal(false)} disabled={schedulingPickup} style={{ ...monoBtn('var(--paper)', 'var(--ink)'), flex: 1 }}>Cancel</button>
                            <button onClick={handleSchedulePickup} disabled={schedulingPickup} data-testid="confirm-pickup-button" style={{ ...monoBtn('var(--a2)', 'var(--o2)'), flex: 1, opacity: schedulingPickup ? 0.7 : 1 }}>
                                {schedulingPickup ? 'Scheduling…' : 'Confirm pickup'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Pause modal */}
            {pauseModalOpen && (
                <div style={overlay} onClick={() => setPauseModalOpen(false)}>
                    <div style={modalBox} onClick={(e) => e.stopPropagation()} data-testid="pause-modal">
                        <h3 style={{ fontFamily: serifDisplay, fontWeight: 400, fontSize: 26, margin: '0 0 18px' }}>Request a pause</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                            <div>
                                <div style={{ ...monoLabel, color: 'var(--a1)', marginBottom: 6 }}>From</div>
                                <input type="date" value={pauseStart} onChange={(e) => setPauseStart(e.target.value)} style={{ ...inputStyle, width: '100%' }} data-testid="pause-start-input" />
                            </div>
                            <div>
                                <div style={{ ...monoLabel, color: 'var(--a1)', marginBottom: 6 }}>To</div>
                                <input type="date" value={pauseEnd} onChange={(e) => setPauseEnd(e.target.value)} style={{ ...inputStyle, width: '100%' }} data-testid="pause-end-input" />
                            </div>
                        </div>
                        <div style={{ ...monoLabel, color: 'var(--a1)', marginBottom: 6 }}>Reason (optional)</div>
                        <textarea value={pauseReason} onChange={(e) => setPauseReason(e.target.value)} rows={3} style={{ ...inputStyle, width: '100%', resize: 'vertical' }} data-testid="pause-reason-input" />
                        {pauseError && <div style={{ color: 'var(--a4)', fontSize: 14, marginTop: 10 }} data-testid="pause-modal-error">{pauseError}</div>}
                        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                            <button onClick={() => setPauseModalOpen(false)} disabled={pauseSubmitting} style={{ ...monoBtn('var(--paper)', 'var(--ink)'), flex: 1 }}>Cancel</button>
                            <button onClick={submitPauseRequest} disabled={pauseSubmitting} data-testid="submit-pause-btn" style={{ ...monoBtn('var(--a3)', 'var(--o3)'), flex: 1, opacity: pauseSubmitting ? 0.7 : 1 }}>
                                {pauseSubmitting ? 'Submitting…' : 'Submit request'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cancel membership modal */}
            {showCancelModal && (
                <div style={overlay} onClick={() => setShowCancelModal(false)}>
                    <div style={modalBox} onClick={(e) => e.stopPropagation()} data-testid="cancel-subscription-modal">
                        <h3 style={{ fontFamily: serifDisplay, fontWeight: 400, fontSize: 26, margin: '0 0 10px' }}>Cancel your membership?</h3>
                        <p style={{ fontSize: 15, lineHeight: 1.6, margin: '0 0 8px' }}>Your membership stays active until <strong>{endDate ? fmtLong(endDate) : 'the end of the current period'}</strong>. No further charges after that.</p>
                        <p style={{ fontSize: 14, opacity: 0.75, margin: '0 0 20px' }}>Please return any borrowed books before your access ends.</p>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={() => setShowCancelModal(false)} disabled={cancelling} style={{ ...monoBtn('var(--paper)', 'var(--ink)'), flex: 1 }}>Keep membership</button>
                            <button onClick={handleCancelSubscription} disabled={cancelling} data-testid="confirm-cancel-subscription" style={{ ...monoBtn('var(--a4)', 'var(--paper)'), flex: 1, opacity: cancelling ? 0.7 : 1 }}>
                                {cancelling ? 'Cancelling…' : 'Yes, cancel'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PaymentsPanel;
